/**
 * BenefitsList Component
 * 
 * Displays Pro subscription benefits with animated icons.
 * Used across all funnel variants.
 */

import { BENEFITS, type BenefitIcon as BenefitIconType } from './constants';
import { useLanguage } from '@/i18n';
import './BenefitsList.css';

interface BenefitsListProps {
  variant?: 'compact' | 'detailed';
  className?: string;
}

// SVG Icons for benefits
function BenefitIcon({ icon }: { icon: BenefitIconType }) {
  switch (icon) {
    case 'cloud':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
        </svg>
      );
    case 'shield':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <polyline points="9 12 11 14 15 10" />
        </svg>
      );
    case 'sync':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      );
    case 'lock':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      );
    default:
      return null;
  }
}

export function BenefitsList({ variant = 'compact', className = '' }: BenefitsListProps) {
  const { t } = useLanguage();
  return (
    <ul className={`benefits-list benefits-list--${variant} ${className}`}>
      {BENEFITS.map((benefit, index) => (
        <li 
          key={benefit.id} 
          className="benefit-item"
          style={{ animationDelay: `${index * 80}ms` }}
        >
          <span className="benefit-icon">
            <BenefitIcon icon={benefit.icon} />
          </span>
          <span className="benefit-content">
            <span className="benefit-text">{t(benefit.text)}</span>
            {variant === 'detailed' && (
              <span className="benefit-detail">{t(benefit.detail)}</span>
            )}
          </span>
          <span className="benefit-check">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
        </li>
      ))}
    </ul>
  );
}
